"use client";

import { useEffect, useState, useRef } from "react";
import { usePathname, useRouter } from 'next/navigation';
import { Menu, X, ChevronDown } from "lucide-react";
import CastButton from "@/components/CastButton";
import Link from "next/link";
import ResumeDownloadButton from "@/components/ResumeDownloadButton";
import { useAnimation } from "@/contexts/AnimationContext";

// Where the trimmed menu on /blog, its posts and /now sends visitors.
const NEW_SITE_URL = "https://portfolio.pollardjr.com/";

export default function Navigation() {
  const { heroAnimationsComplete, userHasScrolled, setUserHasScrolled } = useAnimation();
  const pathname = usePathname() ?? '/';
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isDemoDropdownOpen, setIsDemoDropdownOpen] = useState(false);
  const [shouldShowNav, setShouldShowNav] = useState(false);
  const [currentHash, setCurrentHash] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownToggleRef = useRef<HTMLButtonElement>(null);

  const isHomePath = pathname === '/' || pathname === '';
  const isNowPath = pathname === '/now' || pathname === '/now/';
  const isBlogPath = pathname === '/blog' || pathname === '/blog/' || pathname.startsWith('/blog/');
  // On these pages the menu is cut down to Now, Blog and a link to the new
  // portfolio: no Resume button, Cast buttons or links into the rest of the site.
  const isTrimmedPath = isNowPath || isBlogPath;
  const isTimelinePath = pathname === '/timeline' || pathname === '/timeline/';
  const isCastDemoPath = pathname === '/cast-demo' || pathname === '/cast-demo/';
  const isMediaDemoPath = pathname === '/media-demo' || pathname === '/media-demo/';

  // Active section indicator for hash-based nav items on the home page.
  // Tracks window.location.hash so clicking Projects/Skills/Contact (or
  // hitting back-button across anchors) shifts the aria-current underline
  // off Home. Re-runs on pathname change so a cross-route nav into /#contact
  // picks up the right active item on mount. Note: pushState (used by
  // scrollToSection) doesn't fire hashchange, so scrollToSection manually
  // updates the state below.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const updateHash = () => setCurrentHash(window.location.hash || '');
    updateHash();
    window.addEventListener('hashchange', updateHash);
    window.addEventListener('popstate', updateHash);
    return () => {
      window.removeEventListener('hashchange', updateHash);
      window.removeEventListener('popstate', updateHash);
    };
  }, [pathname]);

  // Treat empty hash as Home so the underline lives on a sensible default
  // when the page first loads at "/" without any fragment.
  const activeHash = currentHash || '#home';

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 100);
      if (window.scrollY > 50 && !userHasScrolled) setUserHasScrolled(true);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [userHasScrolled, setUserHasScrolled]);

  useEffect(() => {
    const isHomePage = pathname === '/' || pathname === '';
    setShouldShowNav(!isHomePage || heroAnimationsComplete || userHasScrolled);
  }, [heroAnimationsComplete, userHasScrolled, pathname]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDemoDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ESC closes the demos dropdown and returns focus to the toggle button.
  useEffect(() => {
    if (!isDemoDropdownOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsDemoDropdownOpen(false);
        dropdownToggleRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isDemoDropdownOpen]);

  const menuItems = [
    { label: "Home", href: "#home" },
    { label: "Skills", href: "#skills" },
    { label: "Projects", href: "#projects" },
    { label: "Contact", href: "#contact" },
  ];

  const demoPages = [
    { label: "Cast Demo", href: "/cast-demo" },
    { label: "Media Demo", href: "/media-demo" },
  ];

  const scrollToSection = (href: string) => {
    if (pathname === '/' || pathname === '') {
      document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
      // Mirror the browser's native anchor-link behavior — update the URL
      // hash so the address bar reflects where the user is on the page.
      if (href.startsWith('#') && typeof window !== 'undefined') {
        window.history.pushState(null, '', href);
        // pushState doesn't fire hashchange — drive aria-current manually.
        setCurrentHash(href);
      }
    } else {
      router.push('/' + href);
    }
    setIsOpen(false);
  };

  if (isTrimmedPath) {
    return (
      <nav
        aria-label="Blog and Now"
        className={`nav-background transition-all duration-500 ease-in-out ${
          scrolled ? "nav-background-scrolled" : "nav-background-transparent"
        } ${
          shouldShowNav
            ? "opacity-100 translate-y-0"
            : "opacity-0 -translate-y-full pointer-events-none"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center h-16 gap-4">
            <Link
              href="/now"
              className="nav-menu-item"
              aria-current={isNowPath ? 'page' : undefined}
            >
              Now
            </Link>
            <Link
              href="/blog"
              className="nav-menu-item"
              aria-current={isBlogPath ? 'page' : undefined}
            >
              Blog
            </Link>
            <div className="flex-1" />
            <a href={NEW_SITE_URL} className="nav-menu-item">
              Portfolio
            </a>
          </div>
        </div>
      </nav>
    );
  }

  return (
    <nav
      className={`nav-background transition-all duration-500 ease-in-out ${
        scrolled ? "nav-background-scrolled" : "nav-background-transparent"
      } ${
        shouldShowNav
          ? "opacity-100 translate-y-0"
          : "opacity-0 -translate-y-full pointer-events-none"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4">

        {/* ── Single flex row: [Resume] [Nav links] [spacer] [Cast buttons] [Mobile btn] ── */}
        <div className="flex items-center h-16 gap-3">

          {/* Resume button — flex item, expands/collapses and pushes nav links naturally */}
          <ResumeDownloadButton />

          {/* Desktop nav links */}
          <div className="hidden md:flex items-center gap-3 ml-1">
            {menuItems.map((item) => (
              <button
                key={item.label}
                onClick={() => scrollToSection(item.href)}
                className="nav-menu-item"
                aria-current={isHomePath && item.href === activeHash ? 'page' : undefined}
              >
                {item.label}
              </button>
            ))}

            <Link
              href="/now"
              className="nav-menu-item"
              aria-current={isNowPath ? 'page' : undefined}
            >
              Now
            </Link>

            <Link
              href="/blog"
              className="nav-menu-item"
              aria-current={isBlogPath ? 'page' : undefined}
            >
              Blog
            </Link>

            <Link
              href="/timeline"
              className="nav-menu-item"
              aria-current={isTimelinePath ? 'page' : undefined}
            >
              Timeline
            </Link>

            {/* Demo Pages dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                ref={dropdownToggleRef}
                onClick={() => setIsDemoDropdownOpen(!isDemoDropdownOpen)}
                className="nav-menu-item flex items-center space-x-1"
                aria-haspopup="menu"
                aria-expanded={isDemoDropdownOpen}
                aria-controls="demos-menu"
              >
                <span>Demos</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${isDemoDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {isDemoDropdownOpen && (
                <div
                  id="demos-menu"
                  role="menu"
                  className="absolute top-full left-0 mt-2 w-56 bg-gray-900/95 backdrop-blur-sm border border-[#39FF14]/20 rounded-lg shadow-lg z-50"
                >
                  <div className="py-2">
                    {demoPages.map((page) => {
                      const isActive =
                        (page.href === '/cast-demo' && isCastDemoPath) ||
                        (page.href === '/media-demo' && isMediaDemoPath);
                      return (
                        <Link
                          key={page.href}
                          href={page.href}
                          role="menuitem"
                          onClick={() => setIsDemoDropdownOpen(false)}
                          aria-current={isActive ? 'page' : undefined}
                          className="block px-4 py-2 text-sm text-gray-300 hover:text-[#39FF14] hover:bg-[#39FF14]/10 transition-colors"
                        >
                          {page.label}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Spacer — pushes cast buttons to far right */}
          <div className="flex-1" />

          {/* Cast buttons — anchored to far right */}
          <div className="hidden md:flex items-center gap-2">
            <div className="cast-button-nav-scale">
              <CastButton variant="outline" size="xs" href="/cast-demo" theme="navigation" ignoreCastState={true}>
                Chromecast Demo
              </CastButton>
            </div>
            <div className="cast-button-nav-scale">
              <CastButton variant="outline" size="xs" href="/media-demo" theme="navigation" ignoreCastState={true}>
                Media Player Demo
              </CastButton>
            </div>
            <div className="cast-button-nav-scale">
              <CastButton variant="outline" size="xs" theme="youtube" functionType="googleCast" />
            </div>
          </div>

          {/* Mobile menu button */}
          <button onClick={() => setIsOpen(!isOpen)} className="md:hidden nav-mobile-button">
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>

        </div>

        {/* Mobile menu drawer */}
        {isOpen && (
          <div className="nav-mobile-menu">
            <div className="px-2 pt-2 pb-3 space-y-1">
              {menuItems.map((item) => (
                <button
                  key={item.label}
                  onClick={() => scrollToSection(item.href)}
                  className="nav-mobile-menu-item"
                  aria-current={isHomePath && item.href === activeHash ? 'page' : undefined}
                >
                  {item.label}
                </button>
              ))}

              <Link
                href="/now"
                onClick={() => setIsOpen(false)}
                aria-current={isNowPath ? 'page' : undefined}
                className="block px-3 py-2 text-sm text-gray-300 hover:text-[#39FF14] hover:bg-[#39FF14]/10 transition-colors rounded"
              >
                Now
              </Link>

              <Link
                href="/blog"
                onClick={() => setIsOpen(false)}
                aria-current={isBlogPath ? 'page' : undefined}
                className="block px-3 py-2 text-sm text-gray-300 hover:text-[#39FF14] hover:bg-[#39FF14]/10 transition-colors rounded"
              >
                Blog
              </Link>

              <Link
                href="/timeline"
                onClick={() => setIsOpen(false)}
                aria-current={isTimelinePath ? 'page' : undefined}
                className="block px-3 py-2 text-sm text-gray-300 hover:text-[#39FF14] hover:bg-[#39FF14]/10 transition-colors rounded"
              >
                Timeline
              </Link>

              <div className="border-t border-[#39FF14]/20 pt-2 mt-2">
                <div className="px-3 py-1 text-xs text-[#39FF14] font-semibold">Demo Pages</div>
                {demoPages.map((page) => {
                  const isActive =
                    (page.href === '/cast-demo' && isCastDemoPath) ||
                    (page.href === '/media-demo' && isMediaDemoPath);
                  return (
                    <Link
                      key={page.href}
                      href={page.href}
                      onClick={() => setIsOpen(false)}
                      aria-current={isActive ? 'page' : undefined}
                      className="block px-3 py-2 text-sm text-gray-300 hover:text-[#39FF14] hover:bg-[#39FF14]/10 transition-colors rounded"
                    >
                      {page.label}
                    </Link>
                  );
                })}
              </div>

              <div className="border-t border-[#39FF14]/20 pt-2 mt-2">
                <div className="px-3 py-2">
                  <CastButton variant="outline" size="sm" theme="youtube" functionType="googleCast" />
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </nav>
  );
}
